import { impactReportDashboard } from 'Modules/Analytics/trackActionInterface/Impact/ImpactReportDashboard'
import { UserSurveyPriority } from 'Generated/graphql-types'
import { analytics } from '../../../../Modules/Analytics'
import { goals as goalsAnalytics } from '../../../../Modules/Analytics/trackActionInterface/Impact/Goals'
import { initiatives } from '../../../../Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { CheckListItemWithGoalInfo } from './types'

export const dispatchGoalTaskDueDateSubmittedEvent = ({
  newDueDate,
  task,
  userId,
}: {
  newDueDate: string | null
  task: CheckListItemWithGoalInfo
  userId: number
}) => {
  const { goal } = task
  const funderSharedGoal = task.goal?.FunderOrgToSharedGoal.find(
    (g) => g.goalId === task.goal?.id && !g.archivedAt
  )

  analytics.trackActionCreator(initiatives.Goal_Task_DueDate_Submitted)({
    dueDate: newDueDate,
    goalId: goal.id,
    goalType: goal.type,
    isGoalSharedWithFunder: !!funderSharedGoal,
    linkText: task.linkText,
    linkUrl: task.linkUrl,
    resourceId: task.resourceId,
    taskDescription: task.title,
    taskId: task.id,
    taskStatus: task.isCompleted ? 'complete' : 'pending',
    taskUpdatedDate: task.updatedAt.substring(0, 10),
    templateLibraryId: goal.templateLibraryId,
    userId,
  })
}

export const dispatchGoalTaskDueDateDeleteClickedEvent = ({
  newDueDate,
  task,
  userId,
}: {
  newDueDate: string | null
  task: CheckListItemWithGoalInfo
  userId: number
}) => {
  const { goal } = task
  const funderSharedGoal = task.goal?.FunderOrgToSharedGoal.find(
    (g) => g.goalId === task.goal?.id && !g.archivedAt
  )

  analytics.trackActionCreator(initiatives.Goal_Task_DueDate_Delete_Clicked)({
    dueDate: newDueDate,
    goalId: goal.id,
    goalType: goal.type,
    isGoalSharedWithFunder: !!funderSharedGoal,
    linkText: task.linkText,
    linkUrl: task.linkUrl,
    resourceId: task.resourceId,
    taskDescription: task.title,
    taskId: task.id,
    taskStatus: task.isCompleted ? 'complete' : 'pending',
    taskUpdatedDate: task.updatedAt.substring(0, 10),
    templateLibraryId: goal.templateLibraryId,
    userId,
  })
}

export const dispatchGoalTaskAssignedEvent = ({
  assigneeUserEmail,
  task,
  newOwnerId,
  userId,
}: {
  assigneeUserEmail: string
  task: CheckListItemWithGoalInfo
  newOwnerId: number | null
  userId: number
}) => {
  const { goal } = task
  const funderSharedGoal = task.goal?.FunderOrgToSharedGoal.find(
    (g) => g.goalId === task.goal?.id && !g.archivedAt
  )

  analytics.trackActionCreator(goalsAnalytics.Goal_Task_Assigned)({
    assigneeUserEmail,
    assigneeUserId: newOwnerId,
    assignerUserId: userId,
    dueDate: task.dueDate ? task.dueDate.slice(0, 10) : null,
    goalUpdatedDate: task.updatedAt.substring(0, 10),
    goalTaskAssignationStatus: newOwnerId ? 'complete' : 'pending',
    goalId: goal.id,
    goalType: goal.type,
    isGoalSharedWithFunder: !!funderSharedGoal,
    taskDescription: task.title,
    taskId: task.id,
    taskStatus: task.isCompleted ? 'complete' : 'pending',
    taskUpdatedDate: task.updatedAt.substring(0, 10),
    templateLibraryId: goal.templateLibraryId,
    userId,
  })
}

export const dispatchGoalTaskAssignedNotificationSentEvent = ({
  task,
  userId,
}: {
  task: CheckListItemWithGoalInfo
  userId: number
}) => {
  const { goal } = task
  const funderSharedGoal = task.goal?.FunderOrgToSharedGoal.find(
    (g) => g.goalId === task.goal?.id && !g.archivedAt
  )

  analytics.trackActionCreator(
    initiatives.Goal_Task_Assigned_Notification_Sent
  )({
    goalId: goal.id,
    goalType: goal.type,
    isGoalSharedWithFunder: !!funderSharedGoal,
    taskId: task.id,
    templateLibraryId: goal.templateLibraryId,
    userId,
  })
}

export const dispatchGoalTaskCompletedEvent = ({
  assigneeUserEmail,
  task,
  userId,
}: {
  assigneeUserEmail: string | null
  task: CheckListItemWithGoalInfo
  userId: number
}) => {
  const { goal } = task
  const funderSharedGoal = task.goal?.FunderOrgToSharedGoal.find(
    (g) => g.goalId === task.goal?.id && !g.archivedAt
  )

  analytics.trackActionCreator(goalsAnalytics.Goal_Task_Completed)({
    assigneeUserEmail,
    dueDate: task.dueDate ? task.dueDate.slice(0, 10) : null,
    isGoalSharedWithFunder: !!funderSharedGoal,
    goalId: goal.id,
    goalType: goal.type,
    resourceId: task.resourceId || null,
    taskDescription: task?.title || null,
    taskId: task.id,
    taskStatus: true,
    taskUpdatedDate: task?.updatedAt ? task.updatedAt.substring(0, 10) : null,
    templateLibraryId: goal?.templateLibraryId,
    userId,
  })
}

export const dispatchImpactReportTeamFilterClicked = ({
  funderOrgId,
  orgId,
  platformPlan,
  userEmail,
  userId,
}: {
  funderOrgId: number | null
  orgId: number
  platformPlan: string
  userEmail: string
  userId: number
}) => {
  analytics.trackActionCreator(
    impactReportDashboard.Report_Teammate_Filter_Clicked
  )({
    funderOrgId,
    orgId,
    platformPlan,
    userEmail,
    userId,
  })
}

export const dispatchImpactReportTeamFilterCleared = ({
  funderOrgId,
  orgId,
  platformPlan,
  userEmail,
  userId,
}: {
  funderOrgId: number | null
  orgId: number
  platformPlan: string
  userEmail: string
  userId: number
}) => {
  analytics.trackActionCreator(
    impactReportDashboard.Report_Teammate_Filter_Cleared
  )({
    funderOrgId,
    orgId,
    platformPlan,
    userEmail,
    userId,
  })
}

export const dispatchImpactReportCategoryFilterClicked = ({
  category,
  funderOrgId,
  orgId,
  platformPlan,
  userEmail,
  userId,
}: {
  category: string | string[]
  funderOrgId: number | null
  orgId: number
  platformPlan: string
  userEmail: string
  userId: number
}) => {
  analytics.trackActionCreator(
    impactReportDashboard.Report_Category_Filter_Clicked
  )({
    category,
    funderOrgId,
    orgId,
    platformPlan,
    userEmail,
    userId,
  })
}

export const dispatchImpactReportFilterByPriorityClicked = ({
  funderOrgId,
  orgId,
  platformPlan,
  priorities,
  userEmail,
  userId,
}: {
  funderOrgId: number | null
  orgId: number
  platformPlan: string
  priorities: UserSurveyPriority[]
  userEmail: string
  userId: number
}) => {
  analytics.trackActionCreator(
    impactReportDashboard.Report_Priorities_Filter_Clicked
  )({
    funderOrgId,
    orgId,
    platformPlan,
    priorities,
    userEmail,
    userId,
  })
}

export const dispatchImpactReportGoalTitleFilterClicked = ({
  funderOrgId,
  goalTitle,
  orgId,
  platformPlan,
  userEmail,
  userId,
}: {
  funderOrgId: number | null
  goalTitle: string | string[]
  orgId: number
  platformPlan: string
  userEmail: string
  userId: number
}) => {
  analytics.trackActionCreator(
    impactReportDashboard.Report_Goal_Title_Filter_Clicked
  )({
    funderOrgId,
    goalTitle,
    orgId,
    platformPlan,
    userEmail,
    userId,
  })
}

export const dispatchClearFiltersClicked = ({
  funderOrgId,
  orgId,
  platformPlan,
  userEmail,
  userId,
}: {
  funderOrgId: number | null
  orgId: number
  platformPlan: string
  userEmail: string
  userId: number
}) => {
  analytics.trackActionCreator(impactReportDashboard.Report_All_Filter_Cleared)(
    {
      funderOrgId,
      orgId,
      platformPlan,
      userEmail,
      userId,
    }
  )
}

export const dispatchGoalClicked = ({
  goalId,
  goalCategory,
  goalTitle,
  funderOrgId,
  orgId,
  platformPlan,
  userEmail,
  userId,
}: {
  goalId: number
  goalCategory: string
  goalTitle: string
  funderOrgId: number | null
  orgId: number
  platformPlan: string
  userEmail: string
  userId: number
}) => {
  analytics.trackActionCreator(impactReportDashboard.Report_Goal_Clicked)({
    goalId,
    goalCategory,
    goalTitle,
    funderOrgId,
    orgId,
    platformPlan,
    userEmail,
    userId,
  })
}

export const dispatchTaskClicked = ({
  goalId,
  goalTitle,
  funderOrgId,
  isCompleted,
  taskId,
  taskTitle,
  orgId,
  platformPlan,
  userEmail,
  userId,
}: {
  goalId: number
  goalTitle: string
  funderOrgId: number | null
  isCompleted: boolean | null | undefined
  taskId: number
  taskTitle: string
  orgId: number
  platformPlan: string
  userEmail: string
  userId: number
}) => {
  analytics.trackActionCreator(impactReportDashboard.Report_Task_Clicked)({
    goalId,
    goalTitle,
    funderOrgId,
    isCompleted,
    taskId,
    taskTitle,
    orgId,
    platformPlan,
    userEmail,
    userId,
  })
}

export const dispatchAcademyResourceClicked = ({
  academyReourceTitle,
  academyResourceType,
  academyResourceCategory,
  academyResourceId,
  funderOrgId,
  orgId,
  platformPlan,
  userEmail,
  userId,
}: {
  academyReourceTitle: string
  academyResourceType: string
  academyResourceCategory: string
  academyResourceId: string
  funderOrgId: number | null
  orgId: number
  platformPlan: string
  userEmail: string
  userId: number
}) => {
  analytics.trackActionCreator(
    impactReportDashboard.Report_Academy_Resource_Clicked
  )({
    academyReourceTitle,
    academyResourceType,
    academyResourceCategory,
    academyResourceId,
    funderOrgId,
    orgId,
    platformPlan,
    userEmail,
    userId,
  })
}

export const dispatchCommentClicked = ({
  commentId,
  commentOwnerId,
  funderOrgId,
  goalId,
  goalTitle,
  orgId,
  platformPlan,
  userEmail,
  userId,
}: {
  commentId: number
  commentOwnerId: number
  funderOrgId: number | null
  goalId: number
  goalTitle: string
  orgId: number
  platformPlan: string
  userEmail: string
  userId: number
}) => {
  analytics.trackActionCreator(impactReportDashboard.Report_Comment_Clicked)({
    commentId,
    commentOwnerId,
    funderOrgId,
    goalId,
    goalTitle,
    orgId,
    platformPlan,
    userEmail,
    userId,
  })
}

export const dispatchProgressUpdateGoalClicked = ({
  goalId,
  goalTitle,
  platformPlan,
  userId,
  progressUpdateValue,
}: {
  goalId: number
  goalTitle: string
  platformPlan: string
  userId: number
  progressUpdateValue: string
}) => {
  analytics.trackActionCreator(
    impactReportDashboard.Report_Progress_Update_Goal_Clicked
  )({
    goalId,
    goalTitle,
    platformPlan,
    userId,
    progressUpdateValue,
  })
}
